import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import * as api from "../../lib/api";

export function TwoFactorSection() {
  const [status, setStatus] = useState<{ enabled: boolean; secret?: string; uri?: string } | null>(
    null,
  );
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    api
      .get2FASetup()
      .then(setStatus)
      .catch(() => {});
  }, []);

  const handleEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.verify2FA(code);
      setMessage("Two-factor authentication enabled");
      setStatus({ enabled: true });
      setCode("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.disable2FA(password);
      setMessage("Two-factor authentication disabled");
      setStatus(null);
      setPassword("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setLoading(false);
    }
  };

  if (!status) return null;

  return (
    <div className="settings-section">
      <p className="settings-eyebrow">Two-Factor Authentication</p>
      <p className="settings-desc">Add an extra layer of security</p>

      {status.enabled ? (
        <form onSubmit={handleDisable}>
          <p className="muted" style={{ marginBottom: 12 }}>2FA is currently enabled.</p>
          <div className="form-group">
            <label className="label">Password (to disable)</label>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && <p className="error-text">{error}</p>}
          {message && <p className="success-text">{message}</p>}
          <div className="form-actions" style={{ justifyContent: "flex-start" }}>
            <button className="btn btn-danger" type="submit" disabled={loading}>
              Disable 2FA
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleEnable}>
          {status.secret && (
            <>
              <p className="muted" style={{ marginBottom: 12 }}>
                Scan this QR code with your authenticator app, then enter the code:
              </p>
              {status.uri && (
                <div className="totp-qr-wrapper">
                  <QRCodeSVG
                    value={status.uri}
                    size={180}
                    level="M"
                    bgColor="transparent"
                    fgColor="currentColor"
                  />
                </div>
              )}
              <details className="totp-manual-entry">
                <summary>Can't scan? Enter manually</summary>
                <code className="code-block" style={{ display: "block", marginTop: 8 }}>
                  {status.secret}
                </code>
              </details>
            </>
          )}
          <div className="form-group">
            <label className="label">Verification code</label>
            <input
              className="input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </div>
          {error && <p className="error-text">{error}</p>}
          {message && <p className="success-text">{message}</p>}
          <div className="form-actions" style={{ justifyContent: "flex-start" }}>
            <button className="btn btn-primary" type="submit" disabled={loading}>
              Enable 2FA
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
